import { RequestConfig } from '@umijs/max';
import { message } from 'antd';
import { fm } from '@/utils/i18nText';

const TOKEN_KEY = 'loginInfo';
const COOKIE_KEY = 'admin_auth';
const COOKIE_DOMAIN = '.chpen.com';

/**
 * 登录态优先级：cookie(.chpen.com 的 admin_auth) 优先于 localStorage(loginInfo)
 *
 * 为什么 cookie 优先（红线：绝不允许上一个账号的身份残留被下一个账号沿用）：
 * 1. cookie 是后端在 .chpen.com 域下发的跨端共享身份，每次登录（含 SSO 端点）都由服务端重写，
 *    代表"服务端当前认可的账号"，也是 APP/官网跳转过来免二次登录的唯一凭据；
 * 2. localStorage 只是浏览器本地残留，很可能停在【上一个账号】（老版本 clearAuth 清不干净、
 *    或用户在其它端切换了账号、或换账号时只清了一处）；
 * 3. 若 localStorage 优先：cookie=A(新账号) + localStorage=B(旧残留) 时会得到 B，
 *    正是"上一个账号的权限被下一个账号沿用"的串号场景，直接踩红线；
 * 4. cookie 优先则永远拿到服务端最新签发的账号。
 * 5. cookie 存在但已过期/损坏 → 一律当未登录并清空残留，【不回退】localStorage：
 *    回退等于复活一个服务端已判定结束的旧会话，同样是串号风险；
 * 6. 仅当 cookie【不存在】时才回退 localStorage，用于兼容"只在 admin 端登录、后端未下发
 *    cookie"的存量情况，不影响已登录用户。
 */

// base64(JSON) —— 必须走 UTF-8 安全解码，否则后端一旦改成 JSON_UNESCAPED_UNICODE 中文用户名就乱码
function b64ToUtf8(raw: string): string {
  const bin = atob(raw);
  const bytes = Uint8Array.from(bin, (c: string) => c.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
}

function utf8ToB64(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function getCookie(name: string): string | null {
  try {
    const all = typeof document !== 'undefined' && document.cookie ? document.cookie : '';
    const parts = all.split(';');
    for (let i = 0; i < parts.length; i++) {
      const seg = parts[i];
      const eq = seg.indexOf('=');
      if (eq < 0) continue;
      if (seg.slice(0, eq).trim() === name) {
        return decodeURIComponent(seg.slice(eq + 1).trim());
      }
    }
    return null;
  } catch {
    return null;
  }
}

type CookieAuth = { token: string; user: any };
// null     = 根本没有 cookie（应回退 localStorage）
// 'invalid' = cookie 存在但过期/损坏（应清空残留、当未登录处理）
type CookieAuthResult = CookieAuth | 'invalid' | null;

function readCookieAuth(): CookieAuthResult {
  const raw = getCookie(COOKIE_KEY);
  if (!raw) return null;
  try {
    const payload = JSON.parse(b64ToUtf8(raw));
    const token = payload?.token;
    if (!token || typeof token !== 'string') return 'invalid';
    const expires = Number(payload?.expires);
    // expires 缺失时按"服务端没给过期时间"处理，交由后端 JWT 校验兜底；给了就必须校验（秒级时间戳）
    if (expires && (!isFinite(expires) || Date.now() / 1000 > expires)) return 'invalid';
    const user = payload?.user ?? (payload?.id != null ? { id: payload.id } : null);
    return { token, user };
  } catch {
    return 'invalid';
  }
}

// cookie 身份写回 localStorage，保证两处一致；账号发生切换时顺带丢掉上一个账号的权限缓存
function applyCookieAuth(auth: CookieAuth) {
  try {
    let old: any = null;
    try {
      const raw = localStorage.getItem(TOKEN_KEY);
      old = raw ? JSON.parse(raw) : null;
    } catch {}
    if (old && old.accessToken === auth.token) return; // 已一致，不做无谓写入，也避免冲掉 roles/permissions 等附加字段
    localStorage.setItem(
      TOKEN_KEY,
      JSON.stringify({ accessToken: auth.token, tokenType: 'Bearer', currentUser: auth.user }),
    );
    localStorage.setItem('token', auth.token);
    // 账号变了：上一个账号的权限/角色缓存必须清掉，否则下一个账号会沿用上一个账号的菜单权限
    localStorage.removeItem('admin_permissions');
    localStorage.removeItem('admin_roles');
  } catch {}
}

function readLocalToken(): string | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    return JSON.parse(raw)?.accessToken || null;
  } catch {
    return null;
  }
}

function readLocalUser(): any {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    return JSON.parse(raw)?.currentUser || null;
  } catch {
    return null;
  }
}

export function getToken(): string | null {
  const cookieAuth = readCookieAuth();
  if (cookieAuth === 'invalid') {
    clearAuth(); // 过期/损坏：清干净残留，不抛异常
    return null;
  }
  if (cookieAuth) {
    applyCookieAuth(cookieAuth);
    return cookieAuth.token;
  }
  return readLocalToken();
}

export function getCurrentUser() {
  const cookieAuth = readCookieAuth();
  if (cookieAuth === 'invalid') {
    clearAuth();
    return null;
  }
  if (cookieAuth) {
    applyCookieAuth(cookieAuth);
    return cookieAuth.user ?? null;
  }
  return readLocalUser();
}

function jwtExpSeconds(token: string): number | null {
  try {
    const seg = token.split('.')[1];
    if (!seg) return null;
    const b64 = seg.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const payload = JSON.parse(b64ToUtf8(b64 + pad));
    const exp = Number(payload?.exp);
    return exp && isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
}

export function setAuth(token: string, user: any) {
  const info = { accessToken: token, tokenType: 'Bearer', currentUser: user };
  localStorage.setItem(TOKEN_KEY, JSON.stringify(info));
  localStorage.setItem('token', token);
  // 同步写一份 .chpen.com 共享 cookie：保证"刚登录完"cookie 与 localStorage 一定是同一个账号，
  // 堵住反方向串号（cookie 停在旧账号、localStorage 是新账号）。
  try {
    const now = Math.floor(Date.now() / 1000);
    const exp = jwtExpSeconds(token) || now + 7 * 24 * 3600;
    const maxAge = Math.max(60, exp - now);
    const value = utf8ToB64(JSON.stringify({ id: user?.id, token, user, expires: exp }));
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; secure' : '';
    document.cookie = `${COOKIE_KEY}=${encodeURIComponent(value)}; path=/; domain=${COOKIE_DOMAIN}; max-age=${maxAge}; SameSite=Lax${secure}`;
  } catch {}
}

export function clearAuth() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('token');
    localStorage.removeItem('admin_auth');
    // 上一个账号的权限/角色缓存，必须一起清，否则下一个账号会沿用上一个账号的菜单权限
    localStorage.removeItem('admin_permissions');
    localStorage.removeItem('admin_roles');
  } catch {}
  try {
    // domain 和 path 必须与下发时完全一致（path=/; domain=.chpen.com），否则清不掉，会留下幽灵 cookie
    document.cookie = `${COOKIE_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${COOKIE_DOMAIN}`;
    // 再补一条不带 domain 的，把 host-only 同名 cookie 也清掉
    document.cookie = `${COOKIE_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  } catch {}
}

export const requestConfig: RequestConfig = {
  baseURL: 'https://api.chpen.com',
  timeout: 30000,
  requestInterceptors: [
    (config: any) => {
      const token = getToken();
      if (token && config.headers) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
  ],
  responseInterceptors: [
    (response: any) => {
      const body = response?.data;
      if (body && body.code === 0 && body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
        if (body.data.data && Array.isArray(body.data.data) && body.data.total !== undefined) {
          body.data.list = body.data.data;
        }
      }
      return response;
    },
  ],
  errorConfig: {
    errorThrower(res: any) {
      if (res?.data?.code !== undefined && res.data.code !== 0 && res.data.code !== 200) {
        throw {
          name: 'BizError',
          message: res.data.msg || res.data.message || fm('请求失败'),
          data: res.data,
          code: res.data.code,
        };
      }
    },
    errorHandler(error: any, opts: any) {
      if (opts?.skipErrorHandler) throw error;
      const status = error?.response?.status;
      const bizCode = error?.code || error?.data?.code;
      const msg =
        error?.data?.msg || error?.data?.message || error?.message || fm('网络错误');

      // #753 自愈通道修复（老陈 2026-10-08 实测：登录页登录失败毫无反馈、永远进不去）
      //
      // 以前只要 HTTP 401 / 业务码 1401、1403 就一律 clearAuth + 跳 /user/login。
      // 但「登录接口自己返回 1401」= 账号或密码错误，并不是会话过期 —— 它也被当成过期处理，
      // 结果是：清空登录态 + 跳回【当前所在的登录页】= 页面毫无变化，用户以为点了没反应。
      // 更糟的是登录成功后若某个接口偶发 1403，也会被踢回登录页，形成"登进去又被弹回"的死循环。
      //
      // 判据（不依赖 error 的内部结构，最稳）：
      //   ① 当前已经在登录页 → 再跳一次毫无意义，只提示真实错误；
      //   ② 出错的是登录类接口 → 同理，把服务端给的文案原样提示给用户。
      const onLoginPage =
        typeof location !== 'undefined' && /\/user\/login/.test(location.pathname || '');
      const errUrl = String(
        error?.config?.url ||
          error?.request?.url ||
          error?.response?.config?.url ||
          error?.response?.url ||
          '',
      );
      const isLoginApi = /auth\/v1\/.*login|auth\/v1\/.*-login|sms-login/.test(errUrl);

      if (onLoginPage || isLoginApi) {
        // ⛔ 绝不 clearAuth：用户可能正拿着有效登录态，清空只会制造新的"被踢下线"
        message.error(msg);
        return;
      }

      // HTTP 401 or business code 1401/1403 means auth failed - redirect to login
      if (status === 401 || bizCode === 1401 || bizCode === 1403) {
        clearAuth(); // 同时清 localStorage 与 .chpen.com 的 admin_auth cookie
        message.warning(fm('登录已过期，请重新登录'));
        window.location.href = '/user/login';
        return;
      }
      message.error(msg);
    },
  },
};
